import "./VideoGeneration.css";
import { CheckOutlined } from "@ant-design/icons";
import { Tabs, Modal, message, Divider, Button, Alert, Tooltip } from "antd";
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useMemo, useState, useEffect, useRef } from "react";
import VideoPreview from "./VideoPreview/VideoPreview";
import { useSelector } from "react-redux";
import { selectSessionId } from "../../../store/sessionSlice";
import { fetchRequest } from "../../../utils/Fetcher";
import { GetVideoSignedUrl } from "../../../utils/GetSignedURL";
import { runVideoGenerationJob } from "../../../utils/polls/RunVideoGenerationJob";

const MAX_RETRY = 3;

const VideoGeneration = ({ storyboardId, onNext, scenes, sceneState, videoData, setVideoData }) => {
  
  const DEFAULT_ROW = {
    approved: false,
    status: "idle",
    videoId: null,
    gcsUri: null,
    url: null,
    error: null,
    signing: false,
  };

  const desc = "Review video generated";
  const RESULT_KEY = "result";

  const [activeKey, setActiveKey] = useState(RESULT_KEY);
  const [isGenerating, setIsGenerating] = useState(false);

  const videoDataRef = useRef(videoData);

  const [mergedStatus, setMergedStatus] = useState("idle");
  const [mergedGcsUri, setMergedGcsUri] = useState(null);
  const [mergedSigning, setMergedSigning] = useState(false);
  const [mergedError, setMergedError] = useState(null);
  const [mergedVideoId, setMergedVideoId] = useState(null);
  const [mergedVideoUrl, setMergedVideoUrl] = useState(null);
  const [isMerging, setIsMerging] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();
  const sessionId = useSelector(selectSessionId);

  const [revisingSceneId, setRevisingSceneId] = useState(null);

  const { sceneIds, sceneById } = useMemo(() => {
    const ids = [];
    const map = new Map();

    (scenes ?? []).forEach((s) => {
      const id = s.id;
      ids.push(id);

      const description = s.description ?? s.sceneDescription ?? "";

      map.set(String(id), { description });
    });

    return { sceneIds: ids, sceneById: map };
  }, [scenes]);

  const isGcsUri = (u) => typeof u === "string" && u.startsWith("gs://");
  const isHttpUrl = (u) => typeof u === "string" && /^https?:\/\//i.test(u);

  const prevKeyRef = useRef(activeKey);

  const approvedCount = (videoData ?? []).filter(
    (v) => v.approved && v.status === "generated" && v.videoId
  ).length;

  const hasMergeStarted = mergedStatus !== "idle";
  const hasMergedResult = mergedStatus === "generated" && Boolean(mergedVideoUrl || mergedGcsUri || mergedVideoId);
  const hasMergedPreview = !!(mergedVideoUrl || mergedVideoId || mergedGcsUri);
  const mergeLoading = isMerging || mergedStatus === "pending" || mergedSigning;
  const mergeLoadingMessage = mergedSigning
    ? "Preparing merged video preview..."
    : mergeLoading
    ? "Merging approved videos..."
    : "";

  useEffect(() => {
    prevKeyRef.current = activeKey;
  }, [activeKey]);

  const videoDataLatestRef = useRef(videoData);
  videoDataLatestRef.current = videoData;


  const upsertScene = (sceneId, patch) => {
    setVideoData((prev) => {
      const list = prev ?? [];
      const idx = list.findIndex((v) => String(v.sceneId) === String(sceneId));
      let next;

      if (idx === -1) next = [...list, { sceneId, approved: false, status: "idle", ...patch }];
      else {
        next = [...list];
        next[idx] = { ...next[idx], ...patch };
      }

      videoDataRef.current = next;
      return next;
    });
  };

  const runPool = async (ids, worker, concurrency = 3) => {
    let i = 0;
    const runners = Array.from(
      { length: Math.min(concurrency, ids.length) },
      () =>
        (async () => {
          while (i < ids.length) {
            const current = ids[i++];
            await worker(current);
          }
        })()
    );
    await Promise.all(runners);
  };

  const buildGeneratePayload = (sceneId) => {
    const cfg = sceneState?.[sceneId] ?? {};

    const referenceImages =
      cfg.setUpValue === "image-reference"
        ? (cfg.refs ?? [])
            .filter((r) => r?.uri)
            .map((r) => ({
              uri: r.uri,
              type: r.type ?? "image",
            }))
        : [];

    return {
      firstFrame:
        cfg.setUpValue === "first-last-frame" ? (cfg.firstFrame?.uri ?? "") : "",
      lastFrame:
        cfg.setUpValue === "first-last-frame" ? (cfg.lastFrame?.uri ?? "") : "",
      aspectRatio: "16:9",
      videoDuration: Number(cfg.durationValue) || 8,
      applyBrandGuideline: cfg.applyBrand ?? true,
      numVideos: 1,
      ...(referenceImages.length > 0 ? { referenceImages } : {}),
    };
  };

  const reviseScene = async (sceneId, revisionInstructions) => {
    const instructions = String(revisionInstructions ?? "").trim();
    if (!instructions) return;

    try {
      setRevisingSceneId(sceneId);
      resetMergedResult();

      const { result } = await fetchRequest({
        path: `sessions/storyboards/${storyboardId}/scenes/${sceneId}/revise`,
        method: "PATCH",
        data: JSON.stringify({
          revisionInstructions: instructions,
        }),
      });

      const revisedScene = result?.data ?? result ?? {};
      const newDescription =
        revisedScene?.description ??
        revisedScene?.sceneDescription ??
        "";

      setVideoData((prev) => {
        const next = (prev ?? []).map((v) =>
          String(v.sceneId) === String(sceneId)
            ? {
                ...v,
                description: newDescription || v.description || "",
                hasPendingDescriptionChange: true,
                error: null,
              }
            : v
        );

        videoDataRef.current = next;
        return next;
      });

      messageApi.success(
        "Scene description revised. You can keep the current video or regenerate a new one."
      );
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message ?? "Failed to revise scene.");
    } finally {
      setRevisingSceneId(null);
    }
  };

  const signAndUpdateScene = async (sceneId, gcsUri) => {
    upsertScene(sceneId, { signing: true });
    try {
      const signed = await GetVideoSignedUrl(gcsUri, 1);
      const signedUrl = signed?.signed_url ?? signed?.url ?? null;
      if (signedUrl) {
        upsertScene(sceneId, { url: signedUrl });
        const sceneIdx = sceneIds.indexOf(sceneId);
        const label = sceneIdx >= 0 ? `Scene ${sceneIdx + 1}` : `Scene ${sceneId}`;
        messageApi.success(`${label} video is ready to view.`);
      }
    } finally {
      upsertScene(sceneId, { signing: false });
    }
  };

  const notifyVideoReady = (sceneId) => {
    const sceneIdx = sceneIds.indexOf(sceneId);
    const label = sceneIdx >= 0 ? `Scene ${sceneIdx + 1}` : `Scene ${sceneId}`;
    messageApi.success(`${label} video is ready to view.`);
  };

  const attemptGenerate = async (sceneId, forceRegenerate = false) => {
    const payload = buildGeneratePayload(sceneId);
    const result = await runVideoGenerationJob({ storyboardId, sceneId, body: payload, forceRegenerate });

    if (result.state === "FAILED") {
      const err = new Error(result.error ?? "Video generation failed");
      err.suggestion = result.suggestion;
      throw err;
    }

    upsertScene(sceneId, {
      approved: false,
      videoId: result.videoId ?? null,
      gcsUri: result.gcsUri ?? null,
      url: result.url ?? null,
      status: "generated",
      error: null,
      hasPendingDescriptionChange: false,
    });

    if (result.url) notifyVideoReady(sceneId);
    if (result.gcsUri && !result.url) await signAndUpdateScene(sceneId, result.gcsUri);
  };

  const generateVideoForScene = async (sceneId, forceRegenerate = false) => {
    resetMergedResult();

    upsertScene(sceneId, {
      status: "pending",
      videoId: null,
      gcsUri: null,
      url: null,
      error: null,
      signing: false,
    });

    let lastError = null;

    for (let attempt = 0; attempt < MAX_RETRY; attempt++) {
      try {
        await attemptGenerate(sceneId, forceRegenerate);
        return;
      } catch (e) {
        lastError = e;
      }
    }

    upsertScene(sceneId, {
      status: "failed",
      error: lastError?.message ?? "Failed to generate video",
      suggestion: lastError?.suggestion ?? null,
    });
    messageApi.error(`Failed to generate video for scene ${sceneId}`);
  };

  const unwrap = (result) => {
    if (!result) return null;
    if (result?.data !== undefined) return result.data;
    return result;
  };

  const isSuccess = (result) => result?.status === "success" && Number(result?.code) === 200;

  const parseStatus = (result) => {
    if (!result) return { state: "PENDING" };

    if (result?.status && !isSuccess(result)) {
      return { state: "FAILED", error: result?.message ?? "Request failed" };
    }

    const data = unwrap(result);
    if (!data) return { state: "PENDING" };

    const id = data?.id ?? data?.videoId ?? data?.video_id ?? null;
    const stateValue = String(
      data?.state ?? data?.status ?? data?.jobStatus ?? ""
    ).toUpperCase();

    const signedUrl =
      data?.signed_url ??
      data?.signedUrl ??
      data?.url ??
      data?.video_url ??
      null;

    const gcsUri =
      data?.gcsUri ??
      data?.gcs_uri ??
      data?.resultLinks?.[0] ??
      null;

    if (isHttpUrl(signedUrl)) {
      return { state: "SUCCEEDED", id, url: signedUrl, gcsUri: isGcsUri(gcsUri) ? gcsUri : null };
    }

    if (isGcsUri(gcsUri)) {
      return { state: "SUCCEEDED", id, gcsUri };
    }

    if (stateValue === "FAILED" || stateValue === "ERROR") {
      return { state: "FAILED", id, error: data?.error ?? data?.message ?? "Request failed" };
    }

    if (stateValue === "SUCCEEDED" || stateValue === "COMPLETED" || stateValue === "DONE") {
      return { state: "SUCCEEDED", id };
    }

    return { state: "PENDING", id };
  };

  const fetchVideoStatus = async (videoId) => {
    try {
      const { result } = await fetchRequest({
        path: `sessions/storyboards/${storyboardId}/scenes/videos/${videoId}`,
        method: "GET",
      });

      return parseStatus(result); 
    } catch (e) {
      return {
        state: "FAILED",
        error: e?.message ?? "Failed to check video status",
      };
    }
  };

  const applyStatusToScene = (sceneId, info, fallbackVideoId) => {
    if (info.state === "SUCCEEDED") {
      upsertScene(sceneId, {
        status: "generated",
        ...(info.id ? { videoId: info.id } : (fallbackVideoId ? { videoId: fallbackVideoId } : {})),
        gcsUri: info.gcsUri ?? null,
        error: null,
      });
    } else if (info.state === "FAILED") {
      upsertScene(sceneId, { status: "failed", error: info.error ?? "Failed" });
    } else {
      upsertScene(sceneId, { status: "pending" });
    }
  };

  const refreshJobsOnce = async () => {
    const needsCheck = (videoDataRef.current ?? []).filter((v) => {
      if (!v.videoId) return false;
      if (v.status === "pending" || v.status === "idle") return true;
      if (v.status === "generated" && (!v.url || isGcsUri(v.url))) return true;
      return false;
    });

    if (!needsCheck.length) return {};

    const entries = await Promise.all(
      needsCheck.map(async (v) => {
        const info = await fetchVideoStatus(v.videoId);
        return [String(v.sceneId), { ...info, videoId: info.id ?? v.videoId }];
      })
    );

    return Object.fromEntries(entries);
  };

  const signingInProgressRef = useRef(new Set());

  const signIfReady = async () => {
    const rows = videoDataRef.current ?? [];

    const toSign = rows.filter(v =>
      v.status === "generated" &&
      isGcsUri(v.gcsUri) &&
      !v.signing &&
      !(v.url && isHttpUrl(v.url)) &&
      !signingInProgressRef.current.has(String(v.sceneId)) // ← guard tambahan
    ).map(v => ({ sceneId: v.sceneId, gcsUri: v.gcsUri }));

    if (!toSign.length) return;

    await runPool(toSign, async ({ sceneId, gcsUri }) => {
      signingInProgressRef.current.add(String(sceneId)); // ← lock sebelum async
      upsertScene(sceneId, { signing: true });
      try {
        const signed = await GetVideoSignedUrl(gcsUri, 1);
        const signedUrl = signed?.signed_url ?? signed?.url ?? null;
        if (signedUrl) {
          upsertScene(sceneId, { url: signedUrl });
        }
      } finally {
        signingInProgressRef.current.delete(String(sceneId)); // ← unlock
        upsertScene(sceneId, { signing: false });
      }
    }, 3);
  };

  useEffect(() => {
    let cancelled = false;

    const initAndGenerate = async () => {
      if (!sessionId || !storyboardId || !sceneIds.length) {
        setActiveKey(RESULT_KEY);
        return;
      }

      const prevList = videoDataLatestRef.current ?? [];
      const prevMap = new Map();
      for (const element of prevList) {
        const key = String(element.sceneId ?? element.scene_id ?? "");
        if (key) prevMap.set(key, element);
      }

      const normalisedRows = [];
      for (const element of sceneIds) {
        const existing = prevMap.get(String(element));
        const meta = sceneById.get(String(element)) ?? { description: "" };
        normalisedRows.push({
          sceneId: element,
          ...DEFAULT_ROW,
          hasPendingDescriptionChange: false,
          ...existing,
          sceneId: element,
          description: (existing?.description ?? meta.description) || "",
        });
      }

      const existingIds = new Set(prevList.map((v) => String(v.sceneId ?? v.scene_id ?? "")));
      const hasNewRows = sceneIds.some((id) => !existingIds.has(String(id)));
      if (hasNewRows) {
        videoDataRef.current = normalisedRows;
        setVideoData(normalisedRows);
      } else {
        videoDataRef.current = prevList;
      }

      const hydratedMerged = window.__hydratedMergedVideo;
      let startAtResult = false;

      if (hydratedMerged?.videoId) {
        delete window.__hydratedMergedVideo;
        setMergedVideoId(hydratedMerged.videoId);
        if (hydratedMerged.gcsUri) setMergedGcsUri(hydratedMerged.gcsUri);
        setMergedStatus("pending");
        startAtResult = true;
        setActiveKey(RESULT_KEY);
      } else {
        setActiveKey(String(sceneIds[0]));
      }

      setIsGenerating(true);
      try {
        const bySceneId = new Map(normalisedRows.map((r) => [String(r.sceneId), r]));

        const needGenerateIds = sceneIds.filter((id) => !bySceneId.get(String(id))?.videoId);

        if (!needGenerateIds.length) {
          videoDataRef.current = normalisedRows;
          setVideoData(normalisedRows);
          setActiveKey(String(sceneIds[0]));
          await signIfReady();
          return;
        }

        await runPool(
          needGenerateIds,
          async (id) => {
            if (cancelled) return;
            await generateVideoForScene(id);
          },
          3
        );
      } finally {
        setIsGenerating(false);
      }
    };

    initAndGenerate();
    return () => { cancelled = true; };
  }, [sessionId, storyboardId, sceneIds.join("|")]);

  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId || !storyboardId) {
      return;
    }

    let cancelled = false;

    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const tick = async (reason = "interval") => {
      if (cancelled) return;

      try {
        const byScene = await refreshJobsOnce();

        Object.entries(byScene).forEach(([sceneId, info]) => {
          applyStatusToScene(sceneId, info, info.videoId);
        });

        await signIfReady();

        const rows = (videoDataRef.current ?? []);
        const hasRows = rows.length > 0;

        if (!hasRows) return;

        const anyWaitingForVideoId = rows.some(v =>
          !v.videoId && (v.status === "pending" || v.status === "idle")
        );
        if (anyWaitingForVideoId) return;

        const allFinal = rows.every(v => v.status === "generated" || v.status === "failed");
        if (allFinal) stop();

      } catch (e) {
        // don't stop on error, keep interval running
      }
    };

    tick("initial");
    timerRef.current = setInterval(() => tick("interval"), 30_000);

    return () => {
      cancelled = true;
      stop();
    };
  }, [sessionId, storyboardId]);

  const signMergedIfNeeded = async (gcsUri) => {
    if (!isGcsUri(gcsUri) || mergedSigning || (mergedVideoUrl && isHttpUrl(mergedVideoUrl))) return;

    setMergedSigning(true);
    try {
      const signed = await GetVideoSignedUrl(gcsUri, 1);
      const signedUrl = signed?.signed_url ?? signed?.url ?? null;
      if (signedUrl) setMergedVideoUrl(signedUrl);
    } finally {
      setMergedSigning(false);
    }
  };

  const handleMergedSucceeded = async (info) => {
    setMergedStatus("generated");
    setMergedError(null);

    if (info.url && isHttpUrl(info.url)) {
      setMergedVideoUrl(info.url);
      messageApi.success("Merged video is ready to view.");
      return;
    }

    const gcs = isGcsUri(info.gcsUri) ? info.gcsUri : mergedGcsUri;
    if (!gcs) return;

    if (!mergedGcsUri) setMergedGcsUri(gcs);
    await signMergedIfNeeded(gcs);
    if (mergedVideoUrl) messageApi.success("Merged video is ready to view.");
  };

  const editSceneDescription = async (sceneId, newDescription) => {
    try {
      const { result } = await fetchRequest({
        method: "PATCH",
        path: `sessions/storyboards/${storyboardId}/scenes/${sceneId}/edit`,
        data: JSON.stringify({ description: newDescription }),
      });

      const updated = result?.data ?? result;

      upsertScene(sceneId, {
        description: updated?.description ?? newDescription,
        hasPendingDescriptionChange: true,
      });

      messageApi.success("Description updated.");
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message ?? "Failed to update description.");
    }
  };

  useEffect(() => {
    if (!sessionId || !storyboardId || !mergedVideoId) return;
    if (mergedStatus !== "pending" && mergedStatus !== "idle") return;

    let cancelled = false;
    let t = null;

    const tick = async () => {
      if (cancelled) return;

      try {
        const info = await fetchVideoStatus(mergedVideoId);

        if (info.state === "SUCCEEDED") {
          await handleMergedSucceeded(info);
          return;
        }

        if (info.state === "FAILED") {
          setMergedStatus("failed");
          setMergedError(info.error ?? "Merge failed");
          return;
        }

        setMergedStatus("pending");
      } catch (e) {
        // don't stop; keep polling
      }

      if (!cancelled) t = setTimeout(tick, 30_000);
    };

    tick();
    return () => {
      cancelled = true;
      if (t) clearTimeout(t);
    };
  }, [sessionId, storyboardId, mergedVideoId]);

  useEffect(() => {
    if (activeKey !== RESULT_KEY) return;
    if (approvedCount < 2 && !hasMergeStarted) {
      if (sceneIds.length > 0) {
        setActiveKey(String(sceneIds[0]));
      }
    }
  }, [activeKey, approvedCount, hasMergeStarted, sceneIds]);

  useEffect(() => {
    const needsSigning = (videoData ?? []).some(
      v => v.status === "generated" && isGcsUri(v.gcsUri) && !v.url && !v.signing
    );
    if (needsSigning && !isGenerating) {
      signIfReady();
    }
  }, [videoData.length]);

  const resetMergedResult = () => {
    setMergedStatus("idle");
    setMergedGcsUri(null);
    setMergedSigning(false);
    setMergedError(null);
    setMergedVideoId(null);
    setMergedVideoUrl(null);
  };

  const toggleApproval = (sceneId) => {
    setVideoData((prev) =>
      prev.map((v) => (v.sceneId === sceneId ? { ...v, approved: !v.approved } : v))
    );
    resetMergedResult();
  };

  const mergeAllVideos = async () => {
    const approved = (videoData ?? []).filter(
      (v) => v.approved && v.status === "generated" && v.videoId
    );

    if (approved.length < 2) {
      messageApi.warning("Approve at least 2 generated scenes first.");
      return;
    }

    try {
      setIsMerging(true);

      setMergedVideoId(null);
      setMergedVideoUrl(null);
      setMergedGcsUri(null);
      setMergedStatus("pending");
      setMergedError(null);

      const { result } = await fetchRequest({
        path: `sessions/storyboards/${storyboardId}/scenes/videos/merge`,
        method: "POST",
        data: JSON.stringify({
          videoIds: approved.map((v) => v.videoId),
        }),
      });

      const finalId = result?.video_id ?? result?.id ?? result?.videoId ?? null;
      const maybeUrl = result?.signed_url ?? result?.url ?? result?.video_url ?? null; 
      const maybeGcs = result?.gcsUri ?? result?.gcs_uri ?? result?.resultLinks?.[0] ?? null;

      if (finalId) setMergedVideoId(finalId);

      if (isHttpUrl(maybeUrl)) {
        setMergedVideoUrl(maybeUrl);
        setMergedStatus("generated");
        messageApi.success("Videos merged successfully!");
        return;
      }

      const gcs = isGcsUri(maybeUrl) ? maybeUrl : (isGcsUri(maybeGcs) ? maybeGcs : null);

      if (gcs) {
        setMergedGcsUri(gcs);
        setMergedSigning(true);
        try {
          const signed = await GetVideoSignedUrl(gcs, 1);
          const signedUrl = signed?.signed_url ?? signed?.url ?? null;

          if (signedUrl) {
            setMergedVideoUrl(signedUrl);
            setMergedStatus("generated");
            messageApi.success("Videos merged successfully!");
            return;
          }
        } finally {
          setMergedSigning(false);
        }
      }

      if (finalId) {
        setMergedStatus("pending");
        messageApi.info("Merge started. Waiting for result...");
        return;
      }

      setMergedStatus("failed");
      setMergedError("Merge response did not include a valid video ID or URL.");
      messageApi.error("Merge response invalid.");
    } catch (e) {
      console.error(e);
      setMergedStatus("failed");
      setMergedError(e?.message ?? "Failed to merge videos.");
      messageApi.error(e?.message ?? "Failed to merge videos.");
    } finally {
      setIsMerging(false);
    }
  };

  const handleTabChange = (nextKey) => {
    if (nextKey !== RESULT_KEY) {
      setActiveKey(nextKey);
      return;
    }

    const approved = (videoData ?? []).filter(
      (v) => v.approved && v.status === "generated" && v.videoId
    );

    if (!approved.length) {
      messageApi.warning("Approve at least 1 generated scene first.");
      setActiveKey(prevKeyRef.current ?? String(sceneIds?.[0] ?? RESULT_KEY));
      return;
    }

    if (mergedStatus === "failed") {
      resetMergedResult();
    }

    if (hasMergeStarted) {
      setActiveKey(RESULT_KEY);
      return;
    }

    Modal.confirm({
      title: "Merge approved videos?",
      content: `This will merge ${approved.length} approved video(s).`,
      okText: "Yes, merge",
      cancelText: "Cancel",
      onOk: async () => {
        setActiveKey(RESULT_KEY);
        await mergeAllVideos();
      },
    });
  };

  const sceneTabs = (videoData ?? []).map((video, idx) => {
    const isRevisingThis = String(revisingSceneId) === String(video.sceneId);
    const needsSigning = video.status === "generated" && video.gcsUri && !video.url;
    const loadingThis = video.status === "pending" || video.status === "idle" || video.signing || needsSigning;

    const isBusy = isRevisingThis || video.status === "pending" || video.signing;

    return {
      key: String(video.sceneId),
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span>{`Scene ${idx + 1}`}</span>
          {video.approved && <CheckOutlined style={{ fontSize: 14 }} />}
        </span>
      ),
      children: (
        <div>
          {video.status === "failed" && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message={`Generate failed for ${video.title ?? `Scene ${idx + 1}`}`}
              description={
                <>
                  <div>{video.error}</div>
                  {video.suggestion && (
                    <div style={{ marginTop: 6, color: "#595959", fontSize: 12 }}>
                      💡 Suggestion: {video.suggestion}
                    </div>
                  )}
                </>
              }
            />
          )}

          <VideoPreview
            loading={loadingThis}
            revising={isRevisingThis}
            videoId={video.videoId}
            url={video.url}
            description={video.description}
            isFailed={video.status === "failed"}
            onApprove={() => toggleApproval(video.sceneId)}
            onRevise={(instructions) => reviseScene(video.sceneId, instructions)}
            approveLabel={video.approved ? "Approved" : "Approve"}
            approveType={video.approved ? "primary" : "default"}
            approveDisabled={video.status !== "generated" || isRevisingThis}
            reviseDisabled={isBusy || video.approved}
            onDescriptionSave={(newDesc) => editSceneDescription(video.sceneId, newDesc)}
            onRegenerate={() => generateVideoForScene(video.sceneId, true)}
            regenerateDisabled={!sessionId || !storyboardId || isBusy || video.approved}
          />
        </div>
      )
    };
  });

  const resultTab = {
    key: RESULT_KEY,
    disabled: approvedCount < 2,
    label: (
      <>
        <Tooltip placement="top" title={process.env.REACT_APP_TOOLTIP_RESULT_TAB_VIDEO_GENERATION}>
          Result
        </Tooltip>
      </>
    ),
    children: (
      <div>
        <div style={{ marginBottom: 12 }}>
          Approved videos: <b>{approvedCount}</b>
        </div>

        {mergeLoading && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={mergeLoadingMessage}
            description="Please wait while we prepare the merged result."
          />
        )}

        {mergedStatus === "failed" ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="Merge failed"
            description={mergedError ?? "Unable to merge videos."}
          />
        ) : hasMergedPreview ? (
          <VideoPreview
            variant="result"
            loading={mergeLoading}
            videoId={mergedVideoId}
            url={mergedVideoUrl}
          />
        ) : (
          !mergeLoading && (
            <Alert
              type="info"
              showIcon
              message="No merged video yet"
              description="Approve some videos, then merge them here."
            />
          )
        )}
      </div>
    ),
  };

  return (
    <div className="video-generation-container">
      {contextHolder}

      <div className="content-title-container">
        <div className="content-title">
          {desc}
          <Tooltip placement="bottom" title={process.env.REACT_APP_TOOLTIP_VIDEO_GENERATION}>
              <QuestionCircleOutlined style={{ marginLeft: 8, fontSize: 14, color: '#888', cursor: 'pointer' }} />
          </Tooltip>
        </div>
        <Divider className="content-title-divider" />
      </div>

      <Tabs
        activeKey={activeKey}
        items={[...sceneTabs, resultTab]}
        onChange={handleTabChange}
        className="tabs-style"
      />

      <div className="back-next-button-vd">
        <Button
          htmlType="submit"
          onClick={onNext}
          disabled={isGenerating || isMerging}
          style={{ visibility: hasMergedResult ? "visible" : "hidden" }}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default VideoGeneration;